import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const APP_STORE_URL = "https://apps.apple.com/app/eqlize-now/id6478133147";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.ten_d";

const DISMISS_UNTIL_KEY = "eqn_app_prompt_dismissed_until";
const DISMISS_DAYS = 14;

const HIDE_ON_PREFIXES = [
  "/sign-in",
  "/login",
  "/forgot-password",
  "/otp-verification",
];

const getClientPlatform = () => {
  if (typeof window === "undefined") return "desktop";

  const ua = navigator.userAgent || navigator.vendor || "";
  const isAndroid = /android/i.test(ua);
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isMobileLike =
    /Mobi|Mobile|Android|iPhone|iPad|iPod/i.test(ua) || isAndroid || isIOS;

  if (!isMobileLike) return "desktop";
  if (isIOS) return "ios";
  if (isAndroid) return "android";
  return "mobile";
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  const iOSStandalone = typeof navigator !== "undefined" && navigator.standalone;
  const displayModeStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches;
  return Boolean(iOSStandalone || displayModeStandalone);
};

const AppDownloadPrompt = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const platform = useMemo(() => getClientPlatform(), []);

  useEffect(() => {
    if (platform === "desktop") {
      setVisible(false);
      return;
    }
    if (isStandalone()) {
      setVisible(false);
      return;
    }
    if (HIDE_ON_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
      setVisible(false);
      return;
    }

    const dismissedUntil = Number(localStorage.getItem(DISMISS_UNTIL_KEY) || 0);
    if (dismissedUntil > Date.now()) {
      setVisible(false);
      return;
    }

    setVisible(true);
  }, [pathname, platform]);

  if (!visible) return null;

  const dismissForNow = () => {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_UNTIL_KEY, String(until));
    setVisible(false);
  };

  const ctaByPlatform = {
    ios: {
      label: "Download on the App Store",
      href: APP_STORE_URL,
    },
    android: {
      label: "Get it on Google Play",
      href: PLAY_STORE_URL,
    },
  };

  const primary = ctaByPlatform[platform];

  return (
    <div className="pointer-events-none fixed bottom-4 left-0 right-0 z-[120] flex justify-center px-3">
      <div className="pointer-events-auto w-full max-w-[520px] rounded-2xl border border-[#d8dde6] bg-white/95 p-4 shadow-[0_18px_50px_rgba(0,0,0,0.2)] backdrop-blur-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#6d7a8c]">
              Better On Mobile
            </p>
            <h3 className="mt-1 text-[18px] font-semibold leading-tight text-[#0f172a]">
              Download the Eqlize Now app
            </h3>
            <p className="mt-1 text-[14px] leading-[1.4] text-[#334155]">
              Chat faster, scan QR codes, and manage your campaign on the go.
            </p>
          </div>
          <button
            type="button"
            aria-label="Dismiss app download prompt"
            onClick={dismissForNow}
            className="rounded-full p-2 text-[#64748b] transition-colors hover:bg-[#f1f5f9] hover:text-[#0f172a]"
          >
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {primary ? (
            <a
              href={primary.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-10 items-center justify-center rounded-xl bg-[#0f172a] px-4 text-[14px] font-semibold text-white transition-colors hover:bg-[#1e293b]"
            >
              {primary.label}
            </a>
          ) : (
            <>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center justify-center rounded-xl bg-[#0f172a] px-4 text-[14px] font-semibold text-white transition-colors hover:bg-[#1e293b]"
              >
                Download on App Store
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center justify-center rounded-xl border border-[#cbd5e1] bg-white px-4 text-[14px] font-semibold text-[#0f172a] transition-colors hover:bg-[#f8fafc]"
              >
                Get it on Google Play
              </a>
            </>
          )}

          <button
            type="button"
            onClick={dismissForNow}
            className="inline-flex h-10 items-center justify-center rounded-xl border border-[#cbd5e1] bg-white px-4 text-[14px] font-semibold text-[#334155] transition-colors hover:bg-[#f8fafc]"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppDownloadPrompt;